import React from 'react'
import { TableColumn, Box, Link as MuiLink } from '@ally/metronome-ui'
import { asCurrency, maskAccountNumber } from '@ally/aos-utils'
import { Account } from '@ally/data-types-afg-accounts'
import { Cell, Row } from 'react-table'
import { Link } from 'react-router-dom'
import { PRODUCT_ID_LOOKUP } from '../../constants'
import { formatBalance, formatClosedDate, formatPercentage } from '../../utils'
import { AccountDetailsLink } from '../Shared'
import { DashboardView } from '../../CustomizeDashboard/types'

/**
 * Helper function for calculating row total
 * @param rows table rows
 * @param key key on the values object that resolves to a number
 * @returns number
 */
function calculateRowTotal(rows: Row<Account>[], key: string): number {
  return rows.reduce((sum, row) => (row.values[key] ?? 0) + sum, 0)
}
interface GetColumnsProps {
  tableType: 'Savings' | 'Checking'
  view: DashboardView
  isLgDown: boolean
  hasStaleData: boolean
  isProductNameChangeEnabled?: boolean
}

interface CellProps {
  cell: Cell<Account>
  row: Row<Account>
}

interface FooterProps {
  rows: Row<Account>[]
}

export const getColumns = ({
  tableType,
  view,
  isLgDown,
  hasStaleData,
  isProductNameChangeEnabled,
}: GetColumnsProps): TableColumn<Account>[] => {
  const interestCheckingProductName = isProductNameChangeEnabled
    ? 'Checking'
    : 'Interest Checking'

  const isSavingsTable = tableType === 'Savings'
  const tableTypeHeader = isSavingsTable
    ? tableType
    : interestCheckingProductName
  const isDefaultView = view === DashboardView.DEFAULT || isLgDown

  const columns = {
    accountNickname: {
      Header: tableTypeHeader,
      Footer: 'Total',
      accessor: 'nickName',
      alignment: 'left',
      isSortable: true,
      Cell: ({ cell, row }: CellProps): React.ReactNode | string => {
        return (
          <>
            <AccountDetailsLink
              account={row.original}
              text={cell.value}
              allytm={
                isSavingsTable
                  ? `deposit-savingsAccount-${row.original.type}`
                  : `deposit-interestCheckingAccount-DDA`
              }
              hasStaleData={hasStaleData}
            />
            <Box
              display="inline-block"
              verticalAlign="middle"
              aria-hidden="true"
            >
              {maskAccountNumber(row.original.accountNumber)}
            </Box>
          </>
        )
      },
    },
    availableBalance: {
      Header: 'Available',
      accessor: 'balance.available',
      alignment: 'right',
      isSortable: true,
      Cell: ({ row, cell }: CellProps): React.ReactNode | string => {
        const acctClosingDate = row.original.closeDate || ''
        const addMoneyAllyTM =
          tableTypeHeader === 'Savings' && row.original.productId
            ? `${PRODUCT_ID_LOOKUP[Number(row.original.productId)]}-add-money`
            : `interest-checking-add-money`
        if (!acctClosingDate && cell.value === 0) {
          return (
            <MuiLink text="Add money" allytmln={addMoneyAllyTM}>
              <Link to="/payments/transfers" />
            </MuiLink>
          )
        }

        return acctClosingDate
          ? formatClosedDate(acctClosingDate)
          : formatBalance(cell.value)
      },
      Footer: ({ rows }: FooterProps): React.ReactNode => {
        const total = React.useMemo(
          () => calculateRowTotal(rows, 'balance.available'),
          [rows],
        )
        return <>{asCurrency(total)}</>
      },
    },
    currentBalance: {
      Header: 'Current',
      accessor: 'balance.current',
      alignment: 'right',
      isSortable: true,
      Cell: ({ cell: { row, value } }: CellProps): React.ReactNode | string =>
        row.original.closeDate ? null : formatBalance(value),
      Footer: ({ rows }: FooterProps): React.ReactNode => {
        const total = React.useMemo(
          () => calculateRowTotal(rows, 'balance.current'),
          [rows],
        )
        return <>{asCurrency(total)}</>
      },
    },
    interestYtd: {
      Header: 'Interest YTD',
      accessor: 'interest.ytd',
      alignment: 'right',
      isSortable: true,
      Cell: ({ cell: { row, value } }: CellProps): React.ReactNode | string =>
        row.original.closeDate ? null : `${asCurrency(value)}`,
      Footer: ({ rows }: FooterProps): React.ReactNode => {
        const total = React.useMemo(
          () => calculateRowTotal(rows, 'interest.ytd'),
          [rows],
        )

        return <>{asCurrency(total)}</>
      },
    },
    apy: {
      Header: 'Annual Percentage Yield',
      accessor: 'interest.apy',
      alignment: 'right',
      isSortable: true,
      Cell: ({ cell: { row, value } }: CellProps): React.ReactNode | string =>
        row.original.closeDate ? null : formatPercentage(value),
    },
    empty: {
      Header: '',
      accessor: 'empty',
      Cell: ({ cell: { row, value } }: CellProps): React.ReactNode | string =>
        null,
    },
  }

  const tileViewCols = [
    columns.accountNickname,
    columns.availableBalance,
    columns.currentBalance,
    columns.empty,
  ] as TableColumn<Account>[]
  const defaultViewCols = [
    columns.accountNickname,
    columns.availableBalance,
    columns.currentBalance,
    columns.interestYtd,
    columns.apy,
  ] as TableColumn<Account>[]

  return isDefaultView ? defaultViewCols : tileViewCols
}
